import type { RumInitConfiguration, TraceType } from '@cloudcare/browser-core'
import { datafluxRum } from '@cloudcare/browser-rum'
import { isbot } from 'isbot'

import type { TranCopInternalUser, TranCopOptions, TranCopUser } from './types'
import { getOriginUrl, getUploadOrigin, isSaaSOrigin } from './utils'

export const version = process.env.npm_package_version

const defaultOptions = {
  version: '0.0.0',
  tracingSampleRate: 100,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  env: 'prod',
  trackUserInteractions: true,
  traceType: 'w3c_traceparent',
  allowedTracingUrls: [/^.+$/],
  ignoreBotTraffic: true,
  storeContextsToLocal: true,
} satisfies Partial<TranCopOptions>

let initialized = false

/**
 * 检查 TranCop 是否已初始化
 *
 * @returns {boolean} 是否已初始化
 */
export function isInitialized(): boolean {
  return initialized
}

/**
 * 初始化成功后的回调
 *
 * 仅浏览器 script 方式引入时有效
 *
 * @param callback
 */
export function onReady(callback: () => void): void {
  if (!initialized) return

  datafluxRum.onReady(callback)
}

/**
 * 设置全局上下文属性，如果属性值为 undefined 或 null，则不设置
 *
 * @param key {string} 属性名
 * @param value {any} 属性值
 */
export function setGlobalContextProperty(key: string, value: unknown): void {
  if (!initialized) return

  if (value === undefined || value === null) {
    return
  }

  datafluxRum.setGlobalContextProperty(key, value)
}

/**
 * 设置全局上下文属性，如果属性值为 undefined 或 null，则不设置
 *
 * @param contexts {Record<string, any>} 属性集合
 */
export function setGlobalContextProperties(contexts: Record<string, unknown>): void {
  if (!initialized) return

  for (const key of Object.keys(contexts)) {
    setGlobalContextProperty(key, contexts[key])
  }
}

/**
 * 移除全局上下文属性
 *
 * @param key {string} 属性名
 */
export function removeGlobalContextProperty(key: string): void {
  if (!initialized) return

  datafluxRum.removeGlobalContextProperty(key)
}

/**
 * 移除全局上下文属性，通过属性名数组
 *
 * @param keys {string[]} 属性名数组
 */
export function removeGlobalContextProperties(keys: string[]): void {
  if (!initialized) return

  for (const key of keys) {
    removeGlobalContextProperty(key)
  }
}

/**
 * 获取全局上下文
 *
 * @returns {Record<string, any>}
 */
export function getGlobalContext(): Record<string, unknown> {
  if (!initialized) return {}

  return datafluxRum.getGlobalContext()
}

let userContextKeys: string[] = []

/**
 * 设置登陆用户信息
 *
 * @param {TranCopUser} 用户信息
 */
export function setUser(user: TranCopUser): void {
  if (!initialized) return

  if (!user) {
    throw new TypeError('[TranCop] "user" is required')
  }

  const {
    companyId = '100',
    depts: _,
    email,
    employeeNo,
    name,
    // source 是内部 key，不需要传递
    source: __,
    uid,
    ...restInfo
  } = user

  datafluxRum.setUser({
    id: employeeNo ?? String(uid),
    name,
    email: email?.toLowerCase(),
    companyId,
    ...restInfo,
  })

  const userContexts = { companyId, employeeNo, ...restInfo }

  setGlobalContextProperties(userContexts)

  userContextKeys = Object.keys(userContexts)
}

/**
 * 获取登陆用户信息
 *
 * @returns {TranCopInternalUser} 内部用户信息
 */
export function getUser(): TranCopInternalUser {
  if (!initialized) return {} as TranCopInternalUser

  return datafluxRum.getUser() as TranCopInternalUser
}

/**
 * 清除登陆用户信息
 */
export function clearUser(): void {
  if (!initialized) return

  removeGlobalContextProperties(userContextKeys)

  datafluxRum.clearUser()

  userContextKeys = []
}

/**
 * 添加自定义活动
 *
 * @param name {string} 任意活动名称
 * @param context {Record<string, any>} 活动上下文
 */
export function addAction(name: string, context?: Record<string, unknown>): void {
  if (!initialized) return

  datafluxRum.addAction(name, context)
}

/**
 * 添加自定义错误
 *
 * @param error {Error | string} 错误对象或错误信息
 * @param context {Record<string, any>} 错误上下文
 */
export function addError(error: Error | string, context?: Record<string, unknown>): void {
  if (!initialized) return

  datafluxRum.addError(error, context)
}

/**
 * 添加自定义遥测时间
 *
 * @param name {string} 时间名称
 * @param time {number} 时间（毫秒）
 */
export function addTiming(name: string, time?: number): void {
  if (!initialized) return

  datafluxRum.addTiming(name, time)
}

/**
 * 暂停跟踪
 */
export function pauseTracking(): void {
  if (!initialized) return

  datafluxRum.stopSessionReplayRecording()
}

/**
 * 恢复跟踪
 */
export function resumeTracking(): void {
  if (!initialized) return

  datafluxRum.startSessionReplayRecording()
}

/**
 * 初始化 TranCop
 *
 * @param opts {TranCopOptions} 配置项
 * @returns
 */
export function initCop(opts: TranCopOptions): void {
  for (const key of ['project', 'service', 'applicationId', 'env'] as const) {
    if (opts[key] === undefined || opts[key] === null || opts[key] === '') {
      throw new TypeError(`[TranCop] "${key}" is required`)
    }
  }

  const {
    applicationId,
    clientToken,
    datakitOrigin,
    env,
    extraContext,
    ignoreBotTraffic,
    project,
    service,
    site,
    storeContextsKey,
    traceType,
    uploadOrigin,
    user,
    ...restOptions
  } = {
    ...defaultOptions,
    ...opts,
  }

  const envValue = typeof env === 'function' ? env() : env

  // 如果 env 为 false，则不初始化
  if (envValue === false) return

  if (ignoreBotTraffic && isbot(navigator.userAgent)) {
    return
  }

  const lowerEnv = envValue.toLowerCase()
  const normalizedOrigin = getUploadOrigin(lowerEnv, uploadOrigin)

  const isSaaS = isSaaSOrigin(normalizedOrigin)

  if (isSaaS && !clientToken) {
    throw new TypeError('[TranCop] "clientToken" is required for SaaS environments')
  }

  const datafluxOptions: RumInitConfiguration = {
    ...restOptions,
    applicationId,
    env: lowerEnv,
    service: service.toLowerCase(),
    traceType: traceType as TraceType,
    storeContextsKey: storeContextsKey ?? applicationId,
    ...(isSaaS
      ? ({
          site: site ?? getOriginUrl(normalizedOrigin),
          clientToken,
        } as any)
      : { datakitOrigin: datakitOrigin ?? getOriginUrl(normalizedOrigin) }),
  }

  datafluxRum.init(datafluxOptions)

  // 需要在初始化之后马上更新标志位，否则下面的 Context 无法正常设置
  initialized = true

  setGlobalContextProperty('project', project.toLowerCase())
  setGlobalContextProperty('namespace', project.toLowerCase())
  setGlobalContextProperty('cop', version)

  const currentUser = typeof user === 'function' ? user() : user

  if (currentUser) {
    setUser(currentUser)
  }

  const extraContextValue =
    (typeof extraContext === 'function' ? extraContext() : extraContext) ?? {}

  setGlobalContextProperties(extraContextValue)

  datafluxRum.startSessionReplayRecording()
}
